import { Head, InfiniteScroll, router, usePage } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  editTripPath,
  loginPath,
  newTripPath,
  passportPath,
  profileFriendshipsPath,
  profilePath,
  profileStorePath,
  rootPath,
  tripPath,
} from "@travily/routes";
import type TripsRow from "@travily/types/models/TripsRow";
import type { TripsRowCanTrait } from "@travily/types/models/TripsRow";
import type FamiliesShow from "@travily/types/pages/FamiliesShow";
import { useConfirmDialog } from "@ui/components/ConfirmDialog";
import { SwooshBackground } from "@ui/components/Swoosh";
import { showToast } from "@ui/components/Toast";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { useShare } from "@ui/hooks/use-share";
import { PROFILE_SWOOSH_COLORS } from "@/components/ProfileFrame/ProfileFrame";
import { useFriendshipActions } from "@/hooks/use-friendship-actions";
import { useNavigateBack } from "@/hooks/use-navigate-back";
import { PageFrame } from "@/layouts/page-frame";
import type { SharedProps } from "@/types";
import { ShowView } from "./show.view";

type TripWithCan = TripsRow & TripsRowCanTrait;

const FamilyShowPage = ({ family, can, trips, badges, has_store, restricted }: FamiliesShow) => {
  const { share } = useShare();
  const { visitModal } = useModalStack();
  const { current_account } = usePage<SharedProps>().props;
  const isOwnProfile = current_account?.handle === family.handle;
  const onBack = useNavigateBack(rootPath());
  const friendshipActions = useFriendshipActions(family);

  // Delete confirmation state — stays client-side (ephemeral UI)
  const deleteTrip = useConfirmDialog<string>(
    (sqid) =>
      new Promise<void>((resolve) => {
        router.delete(tripPath(sqid), { onFinish: () => resolve(), preserveScroll: true });
      }),
  );

  const handleShareProfile = () => {
    share({
      title: family.display_name,
      text: t("web.families.show.share_text", { name: family.display_name }),
      url: `${location.origin}${profilePath(family.handle)}`,
    });
  };

  const handleViewFriends = () => {
    if (!current_account) {
      router.visit(loginPath());
      return;
    }
    // `restricted` is a per-request snapshot, so a profile that flipped private (or got renamed) since
    // render still 404s here — uncaught, that rejection escapes to window.onunhandledrejection.
    visitModal(profileFriendshipsPath(family.handle)).catch(() =>
      showToast.error(t("common.friendships.action_error.title"), t("common.friendships.action_error.description")),
    );
  };

  const showViewProps = {
    badges,
    can,
    deleteState: deleteTrip.dialogProps,
    family,
    onCreateTrip: can?.create_trip ? () => visitModal(newTripPath()) : undefined,
    onDeleteTrip: deleteTrip.request,
    onEditTrip: (trip: TripWithCan) => visitModal(editTripPath(trip.sqid)),
    onShareProfile: handleShareProfile,
    onViewAllBadges: () => visitModal(passportPath(family.handle)),
    onViewFriends: handleViewFriends,
    restricted,
    tabs: has_store
      ? { profileHref: profilePath(family.handle), storeHref: profileStorePath(family.handle) }
      : undefined,
    trips,
    ...friendshipActions,
  };

  const view = <ShowView {...showViewProps} />;

  return (
    <PageFrame
      background={<SwooshBackground className="hidden md:block" color={PROFILE_SWOOSH_COLORS.Family} />}
      mobileTopBar={isOwnProfile ? undefined : <TopBarMobile onBack={onBack} title={`@${family.handle}`} />}
    >
      <Head title={t("web.families.show.meta_title", { name: family.display_name })}>
        <meta name="description" content={t("web.families.show.meta_description", { name: family.display_name })} />
      </Head>
      <InfiniteScroll data="trips" preserveUrl onlyNext>
        {view}
      </InfiniteScroll>
    </PageFrame>
  );
};

export default FamilyShowPage;
